import { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CTASection from "@/components/CTASection";
import { Hammer, Paintbrush, Layers, LayoutGrid, ArrowUpDown, Ruler, PlusCircle } from "lucide-react";
import { motion } from "framer-motion";

// Gallery images for services with photos
import hardwoodImg from "@/assets/gallery/hardwood-install-5.webp";
import sandFinishImg from "@/assets/gallery/sand-finish-11.webp";
import lvpImg from "@/assets/gallery/lvp-install-2.webp";
import laminateImg from "@/assets/gallery/lvp-install-6.webp";
import stairsImg from "@/assets/gallery/stairs-3.webp";

const services = [
  {
    icon: Hammer,
    title: "Hardwood Nail-Down Installation",
    description: "Professional nail-down hardwood flooring installation. We use precision techniques to ensure each plank is securely fastened, creating floors that will last for generations. Our team handles all subfloor preparation and ensures proper acclimation of materials.",
    image: hardwoodImg,
  },
  {
    icon: Paintbrush,
    title: "Sand, Stain & Finish",
    description: "Complete refinishing services using a water-based finishing system. We sand down existing hardwood to remove scratches and wear, apply your choice of stain color, and seal with durable, low-VOC water-based polyurethane for a beautiful, long-lasting finish.",
    image: sandFinishImg,
  },
  {
    icon: Layers,
    title: "LVP Installation",
    description: "Luxury Vinyl Plank installation for a durable, waterproof flooring solution perfect for any room in your home. LVP offers the look of real wood with enhanced durability and moisture resistance, making it ideal for kitchens, bathrooms, and basements.",
    image: lvpImg,
  },
  {
    icon: LayoutGrid,
    title: "Laminate Installation",
    description: "Cost-effective laminate flooring installed with professional precision. Laminate provides a beautiful appearance at a fraction of the cost, with excellent durability for high-traffic areas. We ensure seamless installation with proper expansion gaps and transitions.",
    image: laminateImg,
  },
  {
    icon: ArrowUpDown,
    title: "Stairs: Treads, Risers & More",
    description: "Complete stair work including treads, risers, stringers, and end caps. Whether matching existing hardwood floors or creating a stunning contrast, our stair installations are precise and built to last. We handle everything from simple tread replacements to full staircase renovations.",
    image: stairsImg,
  },
  {
    icon: Ruler,
    title: "Baseboard Removal & Reinstall",
    description: "Careful removal and reinstallation of existing baseboards during floor installation. We take the time to remove baseboards without damage and reinstall them cleanly after your new floors are in place, ensuring a polished final result.",
    image: null,
  },
  {
    icon: PlusCircle,
    title: "New Baseboard Installation",
    description: "Fresh baseboard installation to complete your flooring project perfectly. We offer a variety of baseboard styles and sizes to match your home's aesthetic. Professional installation includes precise cuts, coping for inside corners, and seamless joints.",
    image: null,
  },
];

const ServiceCard = ({ service }: { service: typeof services[0] }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const hasImage = service.image !== null;

  if (!hasImage) {
    // Non-flippable card for services without images
    return (
      <div className="card-service flex gap-6">
        <div className="w-16 h-16 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
          <service.icon className="w-8 h-8 text-accent" />
        </div>
        <div>
          <h3 className="font-heading font-bold text-xl mb-3">{service.title}</h3>
          <p className="text-muted-foreground leading-relaxed">{service.description}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative h-72 cursor-pointer perspective-1000"
      onClick={() => setIsFlipped(!isFlipped)}
    >
      <motion.div
        className="relative w-full h-full"
        initial={false}
        animate={{ rotateY: isFlipped ? 180 : 0 }}
        transition={{ duration: 0.6, ease: "easeInOut" }}
        style={{ transformStyle: "preserve-3d" }}
      >
        {/* Front Side */}
        <div
          className="absolute inset-0 card-service flex gap-6"
          style={{ backfaceVisibility: "hidden" }}
        >
          <div className="w-16 h-16 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
            <service.icon className="w-8 h-8 text-accent" />
          </div>
          <div className="flex flex-col">
            <h3 className="font-heading font-bold text-xl mb-3">{service.title}</h3>
            <p className="text-muted-foreground leading-relaxed text-sm">{service.description}</p>
            <span className="mt-auto text-xs text-accent font-medium pt-3">Click to see our work →</span>
          </div>
        </div>

        {/* Back Side */}
        <div
          className="absolute inset-0 rounded-xl overflow-hidden"
          style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}
        >
          <img
            src={service.image}
            alt={service.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <h3 className="font-heading font-bold text-xl text-white">{service.title}</h3>
            <span className="text-xs text-white/80">Click to flip back</span>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const Services = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="bg-primary py-16">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-primary-foreground mb-4">
              Our Services
            </h1>
            <p className="text-primary-foreground/80 text-lg max-w-2xl mx-auto">
              Professional flooring services delivered with craftsmanship and pride. Every project receives our full attention and expertise.
            </p>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {services.map((service, index) => (
                <ServiceCard key={index} service={service} />
              ))}
            </div>
          </div>
        </section>

        <CTASection />
      </main>
      <Footer />
    </div>
  );
};

export default Services;
